import React, { useEffect, useState } from "react";
import { getProspectMap } from "../../../api/database";

const TEMP_COLOR = { hot: "#34b56a", warm: "#e8923a", cold: "#2f9bd6" };

/**
 * v1 map: plots the agent's own owner records, grouped by street. Streets with
 * a gap concept are illustrated stylistically (matches the prototype). Production
 * can swap this for Mapbox (REACT_APP_MAPBOX_ACCESS_TOKEN already used elsewhere).
 */
export default function ProspectMap({ onOpenRecord, agent }) {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getProspectMap(agent ? { agent } : {})
      .then((res) => setPoints(res.data.points || []))
      .finally(() => setLoading(false));
  }, []);

  // Group by street for a readable layout.
  const byStreet = points.reduce((acc, p) => {
    const key = p.street || p.suburb || "Other";
    (acc[key] = acc[key] || []).push(p);
    return acc;
  }, {});

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-base font-bold text-gray-900">Map view — your records</h3>
        <div className="flex gap-3.5 text-[11.5px] text-gray-500 font-bold items-center">
          {Object.entries(TEMP_COLOR).map(([t, c]) => (
            <span key={t} className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: c }} /> {t}
            </span>
          ))}
        </div>
      </div>
      <p className="text-[12.5px] text-gray-500">
        Your records plotted by street. Pins are coloured by lead temperature. Production uses live maps.
      </p>

      {loading && <div className="text-sm text-gray-400 mt-6">Loading map…</div>}
      {!loading && !points.length && (
        <div className="text-sm text-gray-400 mt-6">No owner records with locations yet. Add records or import a CSV.</div>
      )}

      {Object.entries(byStreet).map(([street, lots]) => (
        <div key={street} className="mt-5">
          <div className="text-[10.5px] uppercase tracking-wider text-gray-400 font-extrabold mb-2">{street}</div>
          <div className="flex gap-2.5 flex-wrap p-4 rounded-lg" style={{ background: "repeating-linear-gradient(90deg,var(--c-panel-soft) 0 60px,var(--c-line-soft) 60px 62px)" }}>
            {lots.map((p) => (
              <button
                key={p.record_id}
                onClick={() => onOpenRecord(p.record_id)}
                className="relative w-[110px] bg-white border border-gray-200 rounded-lg p-2.5 mt-3 text-left hover:border-[#55c5d0]"
              >
                <span
                  className="absolute -top-2 right-2 w-4 h-4 rounded-full border-2 border-white shadow"
                  style={{ background: TEMP_COLOR[p.lead_temp] || "#cfd3d7" }}
                />
                <div className="text-[11px] text-gray-500 font-extrabold truncate">{p.address || "—"}</div>
                <div className="text-[11.5px] text-gray-900 font-bold truncate mt-0.5">{p.name || "Owner"}</div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
